import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import { ChildType } from '../types/on-boarding.type';
import { differenceInYears, parse } from 'date-fns';
import AddGuardianModal from './AddGuardianModal';
import AssignGuardianModal from './AssignGuardianModal';
import {
  useCreateChildren,
  useDeleteChildren,
  useGetChildrenList
} from 'common/queries-and-mutations/children';
import { Loader } from 'components/Loader';
import {
  findNextPage,
  findPreviousPage,
  hasEmptyValue,
  showRemoveButton
} from 'utils';
import { useGetGuardianList } from 'common/queries-and-mutations/guardian';
import AddSubstituteGuardianModal from './AddSubstituteGuardianModal';
import ConfirmModal from 'components/Modal/ConfirmModal';
import { useUpdateStep } from 'common/queries-and-mutations/user';
import { STEPS } from 'constants/steps';
import GenderField from 'components/Form/GenderField';
import withCreatePortal from 'components/HOC/withCreatePortal';
import InfoBar from './InfoBar';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { fetchUserInformation } from 'thunks/account-thunk';

const EnhancedAddGuardianForm = withCreatePortal(AddGuardianModal);
const EnhancedAddSubstituteGuardianForm = withCreatePortal(
  AddSubstituteGuardianModal
);
const EnhancedAssignGuardianForm = withCreatePortal(AssignGuardianModal);
const EnhancedConfirmModal = withCreatePortal(ConfirmModal);

export default function ChildrenInfo() {
  const [showAddGuardian, setShowAddGuardian] = useState(false);
  const [showAddSubsituteGuardian, setShowAddSubsituteGuardian] =
    useState(false);
  const [showAssigneGuardian, setShowAssigneGuardian] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isError, isLoading } = useCreateChildren();
  const { mutate: deleteChildren, isLoading: isDeleting } = useDeleteChildren();
  const {
    mutate: updateStep,
    isSuccess: stepSuccess,
    isError: stepError
  } = useUpdateStep();
  const {
    data: childrens,
    isSuccess: isChildSuccess,
    isLoading: isChildLoading,
    refetch: getChildrens
  } = useGetChildrenList();
  const { data: guardians, refetch: getGuardians } = useGetGuardianList();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const children = useMemo(() => {
    if (isChildSuccess && childrens.length > 0) {
      return childrens.map(child => ({
        childId: child.id,
        childFirstName: child.childFirstName,
        childMiddleName: child.childMiddleName,
        childLastName: child.childLastName,
        gender: child.gender,
        phoneNumber: child.phoneNumber,
        dateOfBirth: child.dateOfBirth,
        hasGuardian: !!child.guardianId
      }));
    } else {
      return [
        {
          childId: null,
          childFirstName: '',
          childMiddleName: '',
          childLastName: '',
          gender: '',
          phoneNumber: '',
          dateOfBirth: '',
          hasGuardian: false
        }
      ];
    }
  }, [childrens, isChildSuccess]);

  const formik = useFormik({
    initialValues: {
      children
    },
    // validationSchema: Yup.object({
    //   children: Yup.array().of(
    //     Yup.object().shape({
    //       childName: Yup.string().required('Child name is required').nullable(),
    //       gender: Yup.string().required('Gender is required').nullable(),
    //       dateOfBirth: Yup.date().required('Date of birth is required').nullable()
    //     })
    //   )
    // }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      const excludedFields = [
        'childId',
        'phoneNumber',
        'hasGuardian',
        'childMiddleName'
      ]; // Fields to exclude from validation
      if (hasEmptyValue(values.children, excludedFields)) {
        updateStep(STEPS.CHILDREN_INFORMATION);
      } else {
        onChildSubmit(values);
      }
    }
  });

  const addMoreChild = () => {
    formik.values.children.push({
      childId: null,
      childFirstName: '',
      childMiddleName: '',
      childLastName: '',
      gender: '',
      phoneNumber: '',
      dateOfBirth: '',
      hasGuardian: false
    });
    formik.setFieldValue('children', [...formik.values.children]);
  };

  const deleteChild = (indexToRemove: number, childId: number | null) => {
    if (childId) {
      deleteChildren(childId);
    }
    formik.values.children.splice(indexToRemove, 1);
    formik.setFieldValue('children', [...formik.values.children]);
  };

  const onChildSubmit = (formValue: ChildType) => {
    //Check if their is user that is under 18
    const currentDate = new Date();
    const usersUnder18 = formValue.children.filter(child => {
      const birthdate = parse(child.dateOfBirth, 'yyyy-MM-dd', new Date());
      const age = differenceInYears(currentDate, birthdate);
      return age < 18 && !child.hasGuardian;
    });

    mutate(formValue);

    //Continue to the next page if children is above 18
    if (!usersUnder18.length) {
      navigate(findNextPage(planAllowPage, STEPS.CHILDREN_INFORMATION));
      return;
    }

    // Show modal to create guardian if child is under age
    if (usersUnder18.length) {
      if (guardians?.length) {
        setShowAskToConfirm(true);
      } else {
        setShowAddGuardian(true);
        getChildrens();
      }
    }
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      dispatch(fetchUserInformation());
      setIsSubmitting(false);
      getChildrens();
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, isLater, dispatch]);

  useEffect(() => {
    if (stepSuccess && !stepError) {
      setIsSubmitting(false);
      navigate(findNextPage(planAllowPage, STEPS.CHILDREN_INFORMATION));
      return;
    }
  }, [stepSuccess, stepError, navigate, planAllowPage]);

  //Execute if guardian Successfully create
  const onGuardianComplete = () => {
    getGuardians();
    getChildrens();
    setShowAddGuardian(false);
    setShowAddSubsituteGuardian(true); //Show add substitute
  };

  const onSubsituteGuardianComplete = () => {
    setShowAssigneGuardian(true); //Show Assign guardian
    setShowAddGuardian(false);
    setShowAddSubsituteGuardian(false);
    getChildrens();
  };

  //We use this to get the user after user as been Successfully created
  const underAgeChild = useMemo(() => {
    const currentDate = new Date();
    if (childrens) {
      const usersUnder18AndNoGuardian = childrens.find(child => {
        const birthdate = parse(child.dateOfBirth, 'yyyy-MM-dd', new Date());
        const age = differenceInYears(currentDate, birthdate);
        return age < 18 && !child.guardianId;
      });
      return usersUnder18AndNoGuardian;
    }
  }, [childrens]);

  //Execute on after guardian is assigned
  const onAssignGuardianComplete = () => {
    setShowAssigneGuardian(false);
    navigate(findNextPage(planAllowPage, STEPS.CHILDREN_INFORMATION));
  };

  //Execute this if the children in under age
  const showAskQuestionModal = () => {
    setShowAskToConfirm(true);
    setShowAddGuardian(false);
  };

  const onContinueLater = () => {
    const excludedFields = [
      'childId',
      'phoneNumber',
      'hasGuardian',
      'childMiddleName'
    ]; // Fields to exclude from validation
    if (hasEmptyValue(formik.values.children, excludedFields)) {
      updateStep(STEPS.CHILDREN_INFORMATION);
    } else {
      mutate(formik.values);
    }
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.CHILDREN_INFORMATION));
  };

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={3}
        stepPercentage={STEPS.CHILDREN_INFORMATION}
        timelineStep={1}
      />

      {isChildLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
            Child(ren) Information
          </p>
          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {formik.values.children.map((children, index) => (
              <div key={index} className="my-8">
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`children[${index}].childFirstName`}
                      placeholder=""
                      value={children.childFirstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Child First Name"
                      error={
                        formik.touched.children
                          ? formik.errors.children?.[index] &&
                            formik.errors.children.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.children[index])
                              ).childFirstName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`children[${index}].childMiddleName`}
                      placeholder=""
                      value={children.childMiddleName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Child Middle Name"
                      error={
                        formik.touched.children
                          ? formik.errors.children?.[index] &&
                            formik.errors.children.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.children[index])
                              ).childMiddleName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`children[${index}].childLastName`}
                      placeholder=""
                      value={children.childLastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Child Last Name"
                      error={
                        formik.touched.children
                          ? formik.errors.children?.[index] &&
                            formik.errors.children.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.children[index])
                              ).childLastName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`children[${index}].phoneNumber`}
                      placeholder=""
                      value={children.phoneNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Phone Number"
                      error={
                        formik.touched.children
                          ? formik.errors.children?.[index] &&
                            formik.errors.children.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.children[index])
                              ).phoneNumber
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`children[${index}].dateOfBirth`}
                      placeholder=""
                      value={children.dateOfBirth}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Date of Birth"
                      error={
                        formik.touched.children
                          ? formik.errors.children?.[index] &&
                            formik.errors.children.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.children[index])
                              ).dateOfBirth
                            : ''
                          : ''
                      }
                      type="date"
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                      validateDate={false}
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <GenderField
                      gender={children.gender}
                      onSelect={gender =>
                        formik.setFieldValue(
                          `children[${index}].gender`,
                          gender
                        )
                      }
                    />
                  </div>
                </div>
                {showRemoveButton(
                  index,
                  children.childId,
                  childrens!.length > 1
                ) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() => deleteChild(index, children.childId)}
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            <div className="mt-5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Icon name={Icons.Required} />
                <span className="text-[10px] text-[#7A7C87]">
                  Indicates Required
                </span>
              </div>
              <div
                className="flex items-center gap-4"
                onClick={addMoreChild}
                role="button"
                tabIndex={-1}
              >
                <Icon name={Icons.AddCircleOutline} />
                <span className="mt-2 text-base font-medium text-[#353535]">
                  Add Another Child
                </span>
              </div>
            </div>

            <div className="flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-5 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-10 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type={!underAgeChild ? 'submit' : 'button'}
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                  onClick={() => {
                    if (underAgeChild && guardians?.length) {
                      showAskQuestionModal();
                    } else if (underAgeChild && !guardians?.length) {
                      setShowAddGuardian(true);
                    }
                  }}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}

      {showAddGuardian && (
        <EnhancedAddGuardianForm
          onClose={() => setShowAddGuardian(false)}
          onComplete={() => {
            getGuardians();
            onGuardianComplete();
          }}
        />
      )}

      {showAddSubsituteGuardian && !showAssigneGuardian && (
        <EnhancedAddSubstituteGuardianForm
          onClose={() => setShowAddSubsituteGuardian(false)}
          onComplete={() => {
            setShowAssigneGuardian(true); //Show Assign guardian
            onSubsituteGuardianComplete();
            getGuardians();
          }}
        />
      )}

      {showAssigneGuardian && (
        <EnhancedAssignGuardianForm
          onClose={() => setShowAssigneGuardian(false)}
          onAssignGuardianComplete={onAssignGuardianComplete}
          guardians={guardians}
          childrenId={underAgeChild?.id}
          onAddGuardianClick={() => setShowAddGuardian(true)}
        />
      )}

      {showAskToConfirm && (
        <EnhancedConfirmModal
          content="Would you like to select existing guardian"
          onConfirm={() => {
            setShowAskToConfirm(false);
            setShowAssigneGuardian(true);
          }}
          onCancel={() => {
            setShowAddGuardian(true);
            setShowAskToConfirm(false);
          }}
          actionText="Yes"
          cancelText="No"
          title="Action Required"
        />
      )}
    </div>
  );
}
